import { useMemo } from 'react'
import { POLL } from '@/lib/pollTiers'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Snowflake, Truck, CheckCircle2, AlertTriangle } from 'lucide-react'
import clsx from 'clsx'
import Modal from '@/components/Modal'
import QueryStateGate from '@/components/QueryStateGate'
import SetupPointer from '@/components/SetupPointer'
import { BrandLoader } from '@/components/BrandLoader'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface LaneRow {
  lane_code:  string
  capacity:   number
  is_active:  boolean
  sort_order: number
  notes:      string | null
}

interface OccupantPallet {
  pallet_id:        string
  loading_bay_lane: string
  pallet_type:      string | null
  spec:             string | null
  variety:          string | null       // joined from runs
  destination:      string | null
  pack_type:        string | null
  grade:            string | null
  size_code:        string | null
  packed_at:        string
}

export interface PalletToPlace {
  pallet_id:   string
  pallet_type: string | null
  spec:        string | null
  variety:     string | null
  grade:       string | null
  size_code:   string | null
  pack_type:   string | null
  destination: string | null
  packed_at:   string
}

// ---------------------------------------------------------------------------
// Categorisation helpers
// ---------------------------------------------------------------------------
export function isFreshmarkPallet(p: Pick<PalletToPlace, 'pallet_type'>): boolean {
  const t = (p.pallet_type ?? '').toLowerCase()
  return t.includes('freshmark')
}

export function isExportPallet(p: Pick<PalletToPlace, 'pallet_type' | 'spec'>): boolean {
  return p.pallet_type === 'Export Pallet'
      || p.pallet_type === 'Export'
      || p.spec        === 'Export'
}

export function isTangoVariety(variety: string | null): boolean {
  return (variety ?? '').trim().toLowerCase() === 'tango'
}

// True if this pallet must go on a refrigerated truck.
export function needsReefer(p: PalletToPlace | OccupantPallet): boolean {
  return isFreshmarkPallet(p) || isTangoVariety(p.variety)
}

// Group key for "matching load" heuristic.
// Export:    variety + packed-date (YYYY-MM-DD) + grade + size
// Freshmark: destination + pack_type + grade + size
function groupKey(p: Pick<PalletToPlace, 'pallet_type' | 'spec' | 'variety' | 'grade' | 'size_code' | 'pack_type' | 'destination' | 'packed_at'>): string {
  if (isExportPallet(p)) {
    const date = (p.packed_at ?? '').slice(0, 10)
    return `EXP|${p.variety ?? ''}|${date}|${p.grade ?? ''}|${p.size_code ?? ''}`
  }
  return `FM|${p.destination ?? ''}|${p.pack_type ?? ''}|${p.grade ?? ''}|${p.size_code ?? ''}`
}

function category(p: Pick<PalletToPlace, 'pallet_type' | 'spec'>): 'EXPORT' | 'FRESHMARK' | 'OTHER' {
  if (isExportPallet(p))    return 'EXPORT'
  if (isFreshmarkPallet(p)) return 'FRESHMARK'
  return 'OTHER'
}

// ---------------------------------------------------------------------------
// Main picker
// ---------------------------------------------------------------------------
interface Props {
  pallet:       PalletToPlace
  onCancel:     () => void
  onPick:       (lane_code: string) => void
  busy?:        boolean
  /** When set, treats this as a move from that lane: disables it in the list and
   *  excludes the pallet from occupancy calculations. */
  currentLane?: string | null
}

export default function LanePicker({ pallet, onCancel, onPick, busy = false, currentLane }: Props) {
  const isMove            = !!currentLane
  const newPalletReefer   = needsReefer(pallet)
  const newPalletCategory = category(pallet)
  const newPalletKey      = groupKey(pallet)

  // Fetch lanes + current occupants in parallel.
  const laneDataQ = useQuery({
    queryKey: ['lane-picker-data'],
    queryFn: async () => {
      const [lanesRes, occupantsRes] = await Promise.all([
        supabase.from('loading_bay_lanes')
          .select('*').eq('is_active', true).order('sort_order').order('lane_code'),
        supabase.from('pallets')
          .select('pallet_id, loading_bay_lane, pallet_type, spec, destination, pack_type, grade, size_code, packed_at, runs(variety)')
          // The Floor Map's set (LoadingBay.tsx): inspected pallets still sit in the lane.
          .in('status', ['IN_LOADING_AREA', 'INSPECTED_APPROVED', 'INSPECTED_REJECTED'])
          .not('loading_bay_lane', 'is', null)
          .is('deleted_at', null),
      ])
      if (lanesRes.error) throw lanesRes.error
      if (occupantsRes.error) throw occupantsRes.error

      const occupants: OccupantPallet[] = (occupantsRes.data ?? []).map(row => {
        const r = (row as { runs: { variety: string } | { variety: string }[] | null }).runs
        const variety = Array.isArray(r) ? r[0]?.variety ?? null : r?.variety ?? null
        return { ...row, variety } as OccupantPallet
      })

      // In move mode, pretend this pallet is off the grid so the current lane
      // doesn't "lock itself" via its own occupancy.
      const effectiveOccupants = isMove
        ? occupants.filter(o => o.pallet_id !== pallet.pallet_id)
        : occupants

      return {
        lanes:     (lanesRes.data ?? []) as LaneRow[],
        occupants: effectiveOccupants,
      }
    },
    refetchInterval: POLL.live,
  })
  const { data, isLoading } = laneDataQ

  const laneSummaries = useMemo(() => {
    if (!data) return []
    return data.lanes.map(lane => {
      const inLane     = data.occupants.filter(o => o.loading_bay_lane === lane.lane_code)
      const remaining  = lane.capacity - inLane.length
      const isEmpty    = inLane.length === 0
      const isFull     = remaining <= 0

      // Derive lane's "effective" state from current contents
      const laneHasReefer = inLane.some(o => needsReefer(o))
      const laneCategories = [...new Set(inLane.map(o => category(o)))]
      const laneCategory   = laneCategories.length === 1 ? laneCategories[0] : laneCategories.length > 1 ? 'MIXED' : null
      const matchingCount  = inLane.filter(o => groupKey(o) === newPalletKey).length

      // Compatibility rules:
      //  1. Empty lane → anything goes
      //  2. Non-empty lane → reefer requirement must match
      //  3. Non-empty lane → category must match (never mix Export and Freshmark)
      let incompatibleReason: string | null = null
      if (!isEmpty) {
        if (newPalletReefer && !laneHasReefer) {
          incompatibleReason = 'Lane has non-reefer pallets — Tango/Freshmark needs its own lane'
        } else if (!newPalletReefer && laneHasReefer) {
          incompatibleReason = 'Lane is reefer-locked (existing Tango/Freshmark pallets)'
        } else if (laneCategory && laneCategory !== 'MIXED' && laneCategory !== newPalletCategory) {
          incompatibleReason = `Lane is staging ${laneCategory} pallets — don't mix loads`
        }
      }

      // Score for sort: lower = better
      //  - Recommended (matches grouping): strongly preferred
      //  - Compatible non-empty (same category, different spec): OK
      //  - Empty: offered after partial compatible lanes (to encourage consolidation)
      //  - Incompatible / Full: pushed to bottom, disabled
      let score = 100
      if (incompatibleReason)  score += 1000
      else if (isFull)          score += 800
      else if (matchingCount > 0) score -= 100
      else if (!isEmpty)        score -= 20
      // empty lanes sit at ~100

      // Summary text
      let summary = 'Empty'
      let summaryDetail = 'Ready for any load'
      if (!isEmpty) {
        if (laneCategory === 'MIXED') {
          summary = 'Mixed loads'
          summaryDetail = `${inLane.length} pallets`
        } else if (laneCategory === 'EXPORT') {
          const sample = inLane[0]
          const date = sample.packed_at.slice(0, 10)
          summary = `Export · ${sample.variety ?? '—'}`
          summaryDetail = `Packed ${date}${sample.grade ? ` · Gr ${sample.grade}` : ''}${sample.size_code ? ` · ${sample.size_code}` : ''}`
        } else if (laneCategory === 'FRESHMARK') {
          const sample = inLane[0]
          summary = `Freshmark · ${sample.destination ?? '—'}`
          summaryDetail = `${sample.pack_type ?? ''}${sample.grade ? ` · Gr ${sample.grade}` : ''}${sample.size_code ? ` · ${sample.size_code}` : ''}`
        } else {
          summary = 'Other'
          summaryDetail = `${inLane.length} pallets`
        }
      }

      return {
        lane,
        inLane,
        remaining,
        laneHasReefer,
        laneCategory,
        matchingCount,
        isEmpty,
        isFull,
        incompatibleReason,
        score,
        summary,
        summaryDetail,
      }
    }).sort((a, b) => a.score - b.score)
  }, [data, newPalletCategory, newPalletKey, newPalletReefer])

  return (
    <Modal
      onClose={onCancel}
      size="lg"
      title={isMove ? `Move pallet${currentLane ? ` from ${currentLane}` : ''}` : 'Move to loading bay'}
      footer={() => busy ? (
        <div className="border-t hairline px-5 py-3 text-sm text-fg-muted">
          Placing pallet…
        </div>
      ) : null}>
        {/* Pallet being placed. A <p>, not a second <h2>: the shell's header
            already owns the dialog's one heading (and its accessible name). */}
        <div className="mb-3">
          <p className="text-lg font-bold text-fg">{pallet.pallet_id}</p>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            <span className={clsx('text-xs rounded-full px-2 py-0.5 font-semibold',
              newPalletCategory === 'EXPORT'    ? 'bg-green-900/60 text-green-300'   :
              newPalletCategory === 'FRESHMARK' ? 'bg-purple-900/60 text-purple-300' :
                                                  'surface-raised text-fg-secondary')}>
              {newPalletCategory}
            </span>
            {pallet.variety && (
              <span className="text-xs surface-raised text-fg-secondary rounded-full px-2 py-0.5">
                {pallet.variety}
              </span>
            )}
            {newPalletReefer && (
              <span className="text-xs bg-sky-900/60 text-sky-300 rounded-full px-2 py-0.5 flex items-center gap-1">
                <Snowflake size={10} /> Needs reefer truck
              </span>
            )}
          </div>
        </div>

        {/* Lane list */}
        <div className="space-y-2">
          {isLoading && (
            <div className="flex justify-center py-8">
              <BrandLoader className="w-8 h-8" />
            </div>
          )}

          {!isLoading && laneSummaries.length === 0 && (
            /* The operator is holding a pallet. "No lanes configured — add them
               in Config first" off a failed read sends them to an admin over
               lanes that are already there, and the pallet goes nowhere. */
            <QueryStateGate query={laneDataQ} noun="lanes"
              empty={
                <div className="flex flex-col items-center justify-center py-12 text-fg-muted">
                  <AlertTriangle size={32} className="mb-2 opacity-40" />
                  <p className="text-sm font-medium">No lanes configured</p>
                  <p className="text-xs mt-1 max-w-xs text-center">
                    <SetupPointer target="loadingBayLanes" linkClassName="text-blue-400">{link => <>Add lanes in {link} first.</>}</SetupPointer>
                  </p>
                </div>
              } />
          )}

          {laneSummaries.map(s => {
            const isCurrent   = isMove && s.lane.lane_code === currentLane
            const disabled    = isCurrent || !!s.incompatibleReason || s.isFull
            const recommended = !disabled && s.matchingCount > 0
            return (
              <button
                key={s.lane.lane_code}
                disabled={disabled}
                onClick={() => onPick(s.lane.lane_code)}
                className={clsx(
                  'w-full text-left rounded-2xl border p-3 transition-colors',
                  disabled
                    ? 'bg-canvas/50 hairline opacity-50 cursor-not-allowed'
                    : recommended
                      ? 'bg-green-950/40 border-green-700 hover:bg-green-900/40'
                      : 'surface hairline-strong hover:bg-gray-750 hover:border-line-strong'
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={clsx(
                        'font-bold tabular-nums',
                        recommended ? 'text-green-200' : 'text-fg'
                      )}>{s.lane.lane_code}</p>

                      {/* Effective lane type based on current contents */}
                      {!s.isEmpty && (
                        <span className={clsx(
                          'text-xs rounded-full px-2 py-0.5 flex items-center gap-1 font-semibold',
                          s.laneHasReefer
                            ? 'bg-sky-900/60 text-sky-300'
                            : 'bg-amber-900/60 text-amber-300'
                        )}>
                          {s.laneHasReefer
                            ? <><Snowflake size={10} /> reefer-locked</>
                            : <><Truck size={10} /> non-reefer</>}
                        </span>
                      )}

                      {recommended && (
                        <span className="text-xs bg-green-700 text-white rounded-full px-2 py-0.5 flex items-center gap-1 font-semibold">
                          <CheckCircle2 size={10} /> Recommended
                        </span>
                      )}
                      {isCurrent && (
                        <span className="text-xs bg-raised-hover text-fg-secondary rounded-full px-2 py-0.5 font-semibold">
                          Current
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-fg-secondary mt-0.5 font-medium">{s.summary}</p>
                    <p className="text-xs text-fg-muted">{s.summaryDetail}</p>
                    {s.lane.notes && (
                      <p className="text-xs text-fg-muted italic mt-0.5">{s.lane.notes}</p>
                    )}
                    {s.incompatibleReason && (
                      <p className="text-xs text-red-400 mt-1">{s.incompatibleReason}</p>
                    )}
                    {s.isFull && !s.incompatibleReason && (
                      <p className="text-xs text-red-400 mt-1">Lane is full</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className={clsx('text-lg font-bold tabular-nums',
                      s.remaining === 0 ? 'text-red-400' :
                      s.remaining <= 3  ? 'text-amber-400' :
                                          'text-fg'
                    )}>
                      {s.inLane.length}/{s.lane.capacity}
                    </p>
                    <p className="text-xs text-fg-muted">{s.remaining > 0 ? `${s.remaining} free` : 'full'}</p>
                    {s.matchingCount > 0 && !disabled && (
                      <p className="text-xs text-green-400 mt-0.5">
                        {s.matchingCount} matching
                      </p>
                    )}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
    </Modal>
  )
}
